'use client';
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

function useIsHydrated() {
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional post-mount flip to opt into client-only motion variant
    setHydrated(true);
  }, []);
  return hydrated;
}

/**
 * Scroll-linked parallax (motion.dev `useScroll` + `useTransform`): the child
 * drifts vertically by ±`distance` px as the element crosses the viewport.
 * Meant for decorative artwork only — content should stay put.
 *
 * SSR-safe (plain div until hydration) and inert for reduced-motion users.
 */
export function ScrollParallax({
  children,
  distance = 32,
  className,
}: {
  children: ReactNode;
  distance?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const hydrated = useIsHydrated();
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);

  if (!hydrated || reduce) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div ref={ref} style={{ y }} className={className}>
      {children}
    </motion.div>
  );
}
